import api from 'config/api.config';
import { AxiosResponse } from 'axios';

// Define interfaces for API response data
interface CurrentSubscription {
  name: string;
  description: string;
  status: string;
  isCancelled: boolean;
  isSuspended: boolean;
  isCancelPending: boolean;
  isActive: boolean;
  price: number;
  currency: string;
  startDate: string;
  endDate: string | null;
  nextBillingDate: string | null;
  autoRenew: boolean;
}

interface CancellationInfo {
  cancelledAt: string;
  subscriptionEndDate: string;
  remainingDays: number;
  canReactivate: boolean;
  reactivationBenefit: string;
  previousPlan: {
    name: string;
    monthlyPrice: number;
  };
}

interface PaymentStats {
  totalSpent: number;
  successfulPayments: number;
  averagePayment: number;
  lastPaymentDate: string | null;
  lastPaymentAmount: number;
}

interface UpcomingCharges {
  date: string;
  amount: number;
  description: string;
}

interface BillingSummaryResponse {
  hasActiveSubscription?: boolean;
  currentSubscription: CurrentSubscription;
  cancellationInfo?: CancellationInfo;
  paymentStats: PaymentStats;
  upcomingCharges: UpcomingCharges | null;
}

interface PaymentHistoryItem {
  date: string;
  amount: number;
  transaction_id: string;
  status: string;
  error?: string;
}

interface Payment {
  _id: string;
  invoiceNumber: string;
  subscriptionName: string;
  amount: number;
  status: string;
  paymentMethod: string;
  paymentDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  transactionId: string;
  stripeSubscriptionId: string;
  paymentHistory: PaymentHistoryItem[];
  hasStripeInvoice?: boolean;
  stripeInvoiceUrl?: string;
  stripeInvoicePdfUrl?: string;
}

interface BillingHistoryResponse {
  payments: Payment[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface SubscriptionDetails {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  currency: string;
  numberOfAccounts: number;
  socialAccounts: string[];
}

interface BillingDetails {
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  daysRemaining: number;
  isExpired: boolean;
  nextBillingDate: string | null;
  nextBillingAmount: number;
  autoRenew: boolean;
  lastPaymentDate: string | null;
  lastPaymentAmount: number;
  paymentMethod: string;
  billingCycle?: string;
  billingCycleLabel?: string;
}

interface StripeInfo {
  customerId: string;
  subscriptionId: string;
  subscriptionStatus: string;
}

interface CurrentBillingResponse {
  subscription: SubscriptionDetails;
  billing: BillingDetails;
  stripeInfo: StripeInfo;
}

// Get billing summary
export const getBillingSummary = async (): Promise<BillingSummaryResponse> => {
  try {
    const response: AxiosResponse<BillingSummaryResponse> = await api.get(
      '/api/billing/summary'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch billing summary'
    );
  }
};

// Get billing history with pagination
export const getBillingHistory = async (
  page: number = 1,
  limit: number = 10
): Promise<BillingHistoryResponse> => {
  try {
    const response: AxiosResponse<BillingHistoryResponse> = await api.get(
      '/api/billing/history',
      {
        params: { page, limit },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch billing history'
    );
  }
};

// Get current billing details
export const getCurrentBilling = async (): Promise<CurrentBillingResponse> => {
  try {
    const response: AxiosResponse<CurrentBillingResponse> = await api.get(
      '/api/billing/current'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch current billing details'
    );
  }
};

// Download invoice — handles both Stripe JSON response and direct PDF download
export const triggerPDFDownload = async (paymentId: string, invoiceNumber: string): Promise<void> => {
  try {
    const response: AxiosResponse<Blob> = await api.get(
      `/api/billing/invoice/${paymentId}`,
      { responseType: 'blob' }
    );

    const contentType = response.headers['content-type'];

    if (contentType?.includes('application/json')) {
      // Stripe payment — parse JSON and open invoice URL in new tab
      const text = await (response.data as Blob).text();
      const data = JSON.parse(text);
      window.open(data.invoicePdfUrl || data.invoiceUrl, '_blank');
    } else {
      // Trial/fallback — download the PDF file directly
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to download invoice'
    );
  }
};

// Cancel subscription response interface
interface CancelSubscriptionDetails {
  cancelledAt: string;
  stripeSubscriptionCancelled: boolean;
  socialMediaAccountsDeactivated: number;
  emailSent: boolean;
}

interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  details: CancelSubscriptionDetails;
}

// Cancel subscription
export const cancelSubscription = async (): Promise<CancelSubscriptionResponse> => {
  try {
    const response: AxiosResponse<CancelSubscriptionResponse> = await api.post(
      '/api/payment/cancel-subscription',
      {}
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(
        error.response?.data?.details || error.response?.data?.message || 'No active subscription found'
      );
    }
    throw new Error(
      error.response?.data?.message || 'Failed to cancel subscription'
    );
  }
};

// Reactivate subscription response interfaces
interface ReactivateWithRemainingDaysDetails {
  customerId: string;
  subscriptionPlan: string;
  previousSubscriptionStatus: string;
  remainingDays: number;
  subscriptionEndDate: string;
  nextBillingDate: string;
  stripeSubscriptionId: string;
  reactivationType: 'immediate_with_trial';
}

interface ReactivateWithCheckoutDetails {
  customerId: string;
  subscriptionPlan: string;
  previousSubscriptionStatus: string;
  remainingDays: 0;
  reactivationType: 'checkout_new_subscription';
}

interface ReactivateWithRemainingDaysResponse {
  success: true;
  message: string;
  details: ReactivateWithRemainingDaysDetails;
}

interface ReactivateWithCheckoutResponse {
  success: true;
  message: string;
  sessionId: string;
  url: string;
  details: ReactivateWithCheckoutDetails;
}

export type ReactivateSubscriptionResponse =
  | ReactivateWithRemainingDaysResponse
  | ReactivateWithCheckoutResponse;

interface ReactivateSubscriptionPayload {
  stripePriceId: string;
  subscriptionId?: string;
}

// Reactivate subscription
export const reactivateSubscription = async (
  stripePriceId: string,
  subscriptionId?: string
): Promise<ReactivateSubscriptionResponse> => {
  try {
    const payload: ReactivateSubscriptionPayload = {
      stripePriceId,
    };

    if (subscriptionId) {
      payload.subscriptionId = subscriptionId;
    }

    const response: AxiosResponse<ReactivateSubscriptionResponse> = await api.post(
      '/api/payment/reactivate-subscription',
      payload
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(
        error.response?.data?.details || error.response?.data?.message || 'No cancelled subscription found'
      );
    }
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || 'Invalid request'
      );
    }
    throw new Error(
      error.response?.data?.message || 'Failed to reactivate subscription'
    );
  }
};
